import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

export default function BlogEditor() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [idError, setIdError] = useState("");
  const customIdRef = useRef(null);

  // Fallback to "Review" or specified type, logic logic below handles "A, B, C, D" exclusively
  const initType = type || "A";

  const [formData, setFormData] = useState({
    blogtype: initType,
    customId: "",
    title: "",
    author: "",
    date: new Date().toISOString().split('T')[0],
    rating: 5.0,
    affiliateLink: "",
    buttonText: "Shop Now",
    type: "Review",
    image1: "",
    image2: "",
    image3: "",
    storeSlug: "",
    para1: "",
    para2: "",
    para3: "",
    para4: "",
    heading1: "",
    heading2: ""
  });

  useEffect(() => {
    if (id) {
      setLoading(true);
      fetch(`${process.env.REACT_APP_BASE_URL}/blogs?id=${id}`) // Adjust API URL based on your env
        .then(res => res.json())
        .then(data => {
            setFormData(data);
            setLoading(false);
        })
        .catch(e => {
            console.error(e);
            setLoading(false);
        });
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const url = id 
      ? `${process.env.REACT_APP_BASE_URL}/blogs/${id}`
      : `${process.env.REACT_APP_BASE_URL}/blogs`;
    
    const method = id ? 'PUT' : 'POST';
    
    // Explicitly set the custom ID string if one is provided for creation
    const payload = { ...formData };
    if (!id && payload.customId) {
        try {
            const checkRes = await fetch(`${process.env.REACT_APP_BASE_URL}/blogs/check-id?id=${payload.customId}`);
            if (!checkRes.ok) throw new Error("API Check Failed");
            
            const exists = await checkRes.json();
            if (exists) {
                setIdError("This ID already exists. Please make another one.");
                if (customIdRef.current) {
                    customIdRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    customIdRef.current.focus();
                }
                setLoading(false);
                return;
            } else {
                setIdError("");
            }
        } catch (e) {
            console.error("Error checking blog ID", e);
            setIdError("Could not verify ID. Check server connection.");
            setLoading(false);
            return;
        }
        payload.id = payload.customId;
    }

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        alert("Blog saved successfully!");
        if (!id) {
          navigate(`/edit-blog/${payload.id || payload.customId}`);
        }
      } else {
        alert("Failed to save blog.");
      }
    } catch (error) {
      console.error(error);
      alert("Error saving blog.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">
        {id ? `Edit Blog` : `Create Blog`} <span className="text-[#4d8a82] ml-2">(Style {formData.blogtype})</span>
      </h1>
      
      {loading ? (
        <div>Loading...</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Custom URL Slug/ID {id ? '(Read-only)' : ''}</label>
              <input ref={customIdRef} type="text" name="customId" value={formData.customId || id || ""} onChange={(e) => { handleChange(e); setIdError(""); }} disabled={!!id} className={`mt-1 w-full border p-2 rounded disabled:bg-gray-100 ${idError ? 'border-red-500 bg-red-50 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500' : 'border-gray-300'}`} placeholder="e.g. your-awesome-blog-post" />
              {idError && <p className="text-red-500 text-xs mt-1 font-semibold">{idError}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} required className="mt-1 w-full border border-gray-300 p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Author</label>
              <input type="text" name="author" value={formData.author} onChange={handleChange} required className="mt-1 w-full border border-gray-300 p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Date</label>
              <input type="date" name="date" value={formData.date} onChange={handleChange} required className="mt-1 w-full border border-gray-300 p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Rating (0-5)</label>
              <input type="number" step="0.1" max="5" min="0" name="rating" value={formData.rating} onChange={handleChange} required className="mt-1 w-full border border-gray-300 p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Affiliate Link</label>
              <input type="url" name="affiliateLink" value={formData.affiliateLink || ""} onChange={handleChange} className="mt-1 w-full border border-gray-300 p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Button Text</label>
              <input type="text" name="buttonText" value={formData.buttonText || ""} onChange={handleChange} className="mt-1 w-full border border-gray-300 p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Tag / Category (e.g. "Shopping")</label>
              <input type="text" name="tag" value={formData.tag || ""} onChange={handleChange} required className="mt-1 w-full border border-gray-300 p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Type (e.g. "Review", "Featured")</label>
              <input type="text" name="type" value={formData.type || ""} onChange={handleChange} required className="mt-1 w-full border border-gray-300 p-2 rounded" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Banner / Card Image URL</label>
              <input type="url" name="bannerImage" value={formData.bannerImage || ""} onChange={handleChange} className="mt-1 w-full border border-gray-300 p-2 rounded" placeholder="Image for the blog list card..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Store Slug (Optional)</label>
              <input type="text" name="storeSlug" value={formData.storeSlug || ""} onChange={handleChange} className="mt-1 w-full border border-gray-300 p-2 rounded" placeholder="e.g. nike, adidas" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Short Info / Excerpt (Used on Blog Cards)</label>
              <textarea rows="2" name="info" value={formData.info || ""} onChange={handleChange} required className="mt-1 w-full border border-gray-300 p-2 rounded resize-y" placeholder="Write a brief summary for the blog list cards..."></textarea>
            </div>
          </div>

          <div className="relative">
            <hr className="my-6 border-gray-300" />
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Live Editable Preview</h2>
            <p className="text-gray-500 mb-8">Edit the content directly in the visual preview below. It will appear exactly like this on the frontend.</p>

            {/* Sidebar Card Preview (Displayed on Right on extra-large screens, Bottom on small) */}
            <div className="w-full max-w-[360px] mx-auto 2xl:absolute 2xl:top-20 2xl:-right-[390px] bg-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm mt-12 2xl:mt-0 z-10">
               <h3 className="text-lg font-bold mb-4 text-gray-700 border-b pb-2 text-center uppercase tracking-wide">Blog Card Preview</h3>
               
               <div className="rounded-[24px] overflow-hidden bg-white shadow-xl hover:shadow-2xl transition-shadow h-full border border-gray-200">
                  <div className="hover:opacity-80 transition block w-full relative">
                    <img 
                      src={formData.bannerImage || "https://placehold.co/375x200?text=Card+Banner+Image"} 
                      alt={formData.title} 
                      loading="lazy" 
                      width="375" height="200" 
                      className="thumb text-[20px] text-[#262626] w-full rounded-[1.75rem] p-4 object-cover h-[232px]" 
                    />
                  </div>
                  <div className="p-[24px] pt-0">
                     <div className="blog-heading block text-[20px] hover:text-[#61a99f] leading-tight font-[600] mb-2 transition-colors line-clamp-2 min-h-[50px]">
                         {formData.title || "Your Blog Title"}
                     </div>
                     <div 
                         className="blog-details text-[14px] h-18 opacity-50 text-ellipsis overflow-hidden" 
                         style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical" }}
                     >
                         {formData.info || "Short excerpt or info summary will appear here..."}
                     </div>
                  </div>
               </div>

               <p className="text-xs text-gray-400 mt-4 text-center">
                 This is how the blog will look on the main index grid.
               </p>
            </div>

            {/* Main Article Preview Container */}
            <div className="w-full relative bg-white shadow-xl rounded-xl">
              {formData.blogtype === "A" && (
                <div className="w-full px-4 sm:px-6 py-10 font-sans text-gray-800 border-4 border-dashed border-gray-200 rounded-xl relative bg-white">
                  {/* Hero Section */}
                  <div className="absolute top-2 left-2 bg-gray-800 text-white text-xs px-2 py-1 rounded shadow">Style A Preview</div>
                  <div className="text-center md:px-12 mb-10 mt-8">
                    <span className="inline-block bg-[#61a99f] text-white text-[10px] font-bold uppercase tracking-wider py-1 px-4 rounded-full mb-4">
                      <i className="fa-solid fa-bag-shopping mr-1.5"></i> {formData.tag || 'Category'}
                    </span>
                    <h1 className="text-3xl md:text-5xl font-bold font-serif mb-4 text-transparent bg-clip-text bg-gradient-to-b from-[#7ac4bc] to-[#4d8a82]">
                      {formData.title || 'Your Title Here'}
                    </h1>
                    <div className="text-gray-500 text-sm mb-6 flex items-center justify-center space-x-4">
                      <span>{formData.date}</span>
                      <span>By {formData.author || 'Author'}</span>
                    </div>
                    <div className="w-full rounded-2xl overflow-hidden shadow-lg h-64 md:h-96 relative bg-gray-100 group">
                        {formData.image1 ? <img src={formData.image1} alt="Hero" className="w-full h-full object-cover" /> : <div className="flex h-full items-center justify-center text-gray-400">Hover to add Image URL</div>}
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-60 opacity-0 group-hover:opacity-100 transition duration-300">
                           <label className="text-white font-bold mb-2">Hero Image URL (image1)</label>
                           <input type="text" name="image1" value={formData.image1 || ""} onChange={handleChange} className="w-3/4 p-2 text-black rounded outline-none" placeholder="Paste image URL here" />
                        </div>
                    </div>
                  </div>

                  {/* Intro Text */}
                  <div className="bg-[#eef6f5] border-l-4 border-[#61a99f] rounded-r-lg p-6 mb-10 shadow-sm transition hover:shadow-md">
                     <label className="text-xs text-[#61a99f] font-bold uppercase mb-2 block">Intro Paragraph (para1 - Markdown)</label>
                     <textarea rows="4" name="para1" value={formData.para1 || ""} onChange={handleChange} className="w-full bg-transparent border-b border-dashed border-[#a0d2cc] focus:outline-none focus:border-[#61a99f] text-gray-700 text-lg leading-relaxed placeholder-[#a0d2cc] resize-y" placeholder="Write the introduction here..."></textarea>
                  </div>

                  {/* Dynamic Points Editor */}
                  <div className="mb-12 mt-8">
                    <h3 className="text-lg font-bold mb-4 text-gray-700 border-b pb-2">Article Points / Steps</h3>
                    {(formData.points || []).map((point, index) => (
                        <div key={index} className="relative border border-[#c8e6e3] p-6 rounded-lg mb-6 bg-white shadow-sm hover:shadow transition">
                           <button onClick={(e) => { e.preventDefault(); const newPoints = [...formData.points]; newPoints.splice(index, 1); setFormData({...formData, points: newPoints}) }} className="absolute top-2 right-2 text-[#7ac4bc] hover:text-[#4d8a82]" title="Remove Point"><i className="fa-solid fa-trash"></i></button>
                           <div className="flex items-center gap-3 bg-[#eef6f5] py-3.5 px-5 rounded-lg mb-5 mt-2">
                             <span className="text-[#61a99f] text-[25px] shrink-0 leading-none">✦</span>
                             <div className="w-8 h-8 rounded-full bg-[#61a99f] text-white font-serif text-[15px] font-bold flex items-center justify-center shrink-0">
                                {index + 1}
                             </div>
                             <input type="text" value={point.title || ""} onChange={(e) => { const newPoints = [...formData.points]; newPoints[index] = {...newPoints[index], title: e.target.value}; setFormData({...formData, points: newPoints}) }} className="w-full bg-transparent border-b border-dashed border-[#a0d2cc] focus:outline-none focus:border-[#61a99f] font-serif font-bold text-lg text-[#4d8a82] m-0 placeholder-[#a0d2cc]" placeholder="Point Title (e.g. First Impressions)" />
                           </div>
                           <div className="px-2">
                              <label className="text-xs text-gray-400 font-bold uppercase mb-2 block">Point Content (Markdown)</label>
                              <textarea rows="5" value={point.content || ""} onChange={(e) => { const newPoints = [...formData.points]; newPoints[index] = {...newPoints[index], content: e.target.value}; setFormData({...formData, points: newPoints}) }} className="w-full bg-transparent border border-dashed border-gray-300 focus:outline-none focus:border-[#7ac4bc] text-gray-600 leading-relaxed text-md p-3 rounded resize-y" placeholder="Write the content for this point..."></textarea>
                           </div>
                        </div>
                    ))}
                    <button onClick={(e) => { e.preventDefault(); setFormData({...formData, points: [...(formData.points || []), { title: "", content: "" }]}) }} className="w-full border-2 border-dashed border-[#a0d2cc] text-[#61a99f] py-3 rounded-lg hover:bg-[#eef6f5] hover:border-[#61a99f] transition font-bold"><i className="fa-solid fa-plus mr-2"></i> Add New Point</button>
                  </div>

                  {/* Pro Tips Box */}
                  <div className="bg-[#eef6f5] border border-[#c8e6e3] rounded-2xl p-6 md:p-8 mb-12 shadow-md hover:shadow-lg transition">
                    <h3 className="text-xl font-bold text-[#4d8a82] mb-6 font-serif">
                       Pro Tips for Savvy Shoppers
                    </h3>
                    <label className="text-xs text-[#7ac4bc] font-bold uppercase mb-2 block">Tips List (para3 - Markdown)</label>
                    <textarea rows="4" name="para3" value={formData.para3 || ""} onChange={handleChange} className="w-full bg-transparent border-dashed border-[#c8e6e3] focus:outline-none focus:border-[#61a99f] text-gray-700 leading-relaxed text-md p-2 resize-y" placeholder="- First tip&#10;- Second tip..."></textarea>
                  </div>

                  {/* Final Verdict */}
                  <div className="bg-[#61a99f] text-white rounded-2xl p-8 mb-4 shadow-xl bg-gradient-to-br from-[#61a99f] to-[#4d8a82]">
                    <h3 className="text-2xl font-bold font-serif mb-4">Final Verdict</h3>
                    <label className="text-xs text-[#c8e6e3] font-bold uppercase mb-2 block">Verdict Text (para4 - Markdown)</label>
                    <textarea rows="4" name="para4" value={formData.para4 || ""} onChange={handleChange} className="w-full bg-transparent text-white border-dashed border-[#a0d2cc] focus:outline-none focus:border-white leading-relaxed text-lg placeholder-[#c8e6e3] resize-y" placeholder="Write the final verdict..."></textarea>
                  </div>
                </div>
              )}

              {formData.blogtype === "B" && (
                <div className="px-4 sm:px-6 py-10 font-sans border-4 border-dashed border-gray-300 rounded-xl relative bg-white">
                   <div className="absolute top-2 left-2 bg-gray-800 text-white text-xs px-2 py-1 rounded shadow z-10">Style B Preview</div>
                   <div className="w-full h-80 md:h-[450px] rounded-xl overflow-hidden mb-8 shadow-2xl relative bg-gray-100 group mt-8">
                       {formData.image1 ? <img src={formData.image1} alt="Hero" className="w-full h-full object-cover" /> : <div className="flex h-full items-center justify-center text-gray-400">Hover to add Hero Image</div>}
                       <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-60 opacity-0 group-hover:opacity-100 transition duration-300">
                          <label className="text-white font-bold mb-2">Hero Image URL (image1)</label>
                          <input type="text" name="image1" value={formData.image1 || ""} onChange={handleChange} className="w-3/4 p-2 text-black rounded outline-none" placeholder="Paste image URL here" />
                       </div>
                       <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex flex-col justify-end p-8 pointer-events-none">
                           <h1 className="font-serif font-bold text-[clamp(24px,5vw,40px)] leading-[1.22] text-white mb-2">{formData.title || 'Your Title Here'}</h1>
                           <div className="font-sans text-[13px] text-white/80">By {formData.author || 'Author'} on {formData.date}</div>
                       </div>
                   </div>
                   <div>
                      <label className="text-xs text-gray-400 font-bold uppercase mb-2 block">Main Content (para1 - Markdown)</label>
                      <textarea rows="14" name="para1" value={formData.para1 || ""} onChange={handleChange} className="w-full font-sans text-[14.5px] leading-[1.85] text-[#4b5563] border border-dashed border-gray-300 p-4 rounded focus:outline-none focus:border-[#7ac4bc] resize-y" placeholder="Start writing the blog post content..."></textarea>
                   </div>
                </div>
              )}

              {formData.blogtype === "C" && (
                <div className="max-w-[1100px] mx-auto px-4 sm:px-6 md:px-10 py-16 md:py-24 font-sans bg-white border-4 border-dashed border-gray-300 rounded-xl relative">
                  <div className="absolute top-2 left-2 bg-gray-800 text-white text-xs px-2 py-1 rounded shadow z-10">Style C Preview (Premium Modernized)</div>
                  
                  {/* Refined Header */}
                  <div className="mb-16 md:mb-20 mt-8">
                    <div className="max-w-[800px] mx-auto text-center">
                      {formData.tag && (
                        <div className="mb-6">
                          <span className="text-[12px] font-bold uppercase tracking-[0.2em] text-[#61a99f]">
                            {formData.tag}
                          </span>
                        </div>
                      )}
                      <h1 className="text-[clamp(36px,5vw,60px)] font-serif font-bold text-[#111] leading-[1.15] mb-8 mx-auto">
                        {formData.title || 'Your Title Here'}
                      </h1>
                      <div className="flex items-center justify-center text-[13px] font-medium text-gray-500 uppercase tracking-widest">
                        <span>By {formData.author || 'Author'}</span>
                        <span className="mx-4 text-gray-300">|</span>
                        <span>{formData.date}</span>
                      </div>
                    </div>
                  </div>

                  {/* Main Wrapped Content */}
                  <div className="max-w-[950px] mx-auto">
                    <div className="clearfix mb-16">
                      <div className="w-full md:w-[50%] md:max-w-[450px] rounded-2xl shadow-xl float-none md:float-left mr-0 md:mr-10 mb-8 md:mb-4 mt-2 object-cover relative group overflow-hidden bg-gray-100 min-h-[250px]">
                         {formData.image1 ? <img src={formData.image1} alt="Wrapped" className="w-full h-full object-cover absolute inset-0" /> : <div className="flex w-full h-full items-center justify-center text-gray-400 text-sm absolute inset-0">Hover to add Wrapped Image</div>}
                         <div className="absolute inset-0 bg-black bg-opacity-60 opacity-0 group-hover:opacity-100 transition duration-300 flex flex-col items-center justify-center">
                            <label className="text-white font-bold text-xs mb-2">Image URL (image1)</label>
                            <input type="text" name="image1" value={formData.image1 || ""} onChange={handleChange} className="w-11/12 p-2 text-black rounded outline-none" placeholder="Paste image URL" />
                         </div>
                      </div>
                      
                      <div className="hover:ring-2 hover:ring-[#61a99f] p-2 rounded transition-all">
                        <label className="text-xs text-[#61a99f] font-bold uppercase mb-2 block clear-none">Top Wrapped Content (para1 - Markdown)</label>
                        <textarea rows="10" name="para1" value={formData.para1 || ""} onChange={handleChange} className="w-full bg-transparent border border-dashed border-gray-300 p-3 font-sans text-[18px] md:text-[20px] leading-[1.9] text-[#333] focus:outline-none focus:border-[#61a99f] resize-y" placeholder="Write the content that wraps around the image..."></textarea>
                      </div>
                    </div>

                    {/* Modern Callout Box (para2) */}
                    <div className="mt-20 hover:ring-2 hover:ring-[#61a99f] transition-all rounded-[2rem] p-2">
                       <label className="text-xs text-[#61a99f] font-bold uppercase mb-2 block p-1">Bottom Callout Box Content (para2 - Markdown)</label>
                       <div className="bg-[#fbfcfa] p-10 md:p-14 rounded-3xl border border-[#e8f0ec] relative">
                         <div className="absolute top-6 left-8 text-[#61a99f]/15 text-7xl font-serif leading-none">❝</div>
                         <textarea rows="6" name="para2" value={formData.para2 || ""} onChange={handleChange} className="w-full bg-transparent border-b border-dashed border-[#bce0dc] p-2 font-serif text-[22px] md:text-[28px] leading-[1.6] text-[#2c524d] italic text-center relative z-10 focus:outline-none focus:border-[#61a99f] resize-y" placeholder="Write content for the quote box..."></textarea>
                       </div>
                    </div>

                    {/* Minimalist Footer Preview */}
                    <div className="mt-24 pt-10 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
                      <div className="flex items-center gap-5">
                         <div className="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 font-serif text-2xl border border-gray-100 shadow-sm">
                           {formData.author ? formData.author.charAt(0).toUpperCase() : 'A'}
                         </div>
                         <div>
                           <div className="text-[15px] font-bold text-[#111] leading-tight mb-1">{formData.author || 'Author'}</div>
                           <div className="text-[11px] text-gray-500 uppercase tracking-widest font-semibold">Written By</div>
                         </div>
                      </div>
                      
                      {formData.tag && (
                        <div className="flex gap-2 flex-wrap sm:justify-end">
                          {formData.tag.split(',').map((t, i) => (
                            <span key={i} className="px-5 py-2 bg-gray-50 text-gray-500 text-[11px] uppercase tracking-widest rounded-full border border-gray-100 font-medium">
                              {t.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {formData.blogtype === "D" && (
                <div className="bg-[#f9f8f6] font-sans border-4 border-dashed border-gray-300 rounded-xl relative pb-20">
                  <div className="absolute top-2 left-2 bg-gray-800 text-white text-xs px-2 py-1 rounded shadow z-10">Style D Preview (Modern Magazine)</div>
                  
                  <div className="max-w-[1200px] mx-auto px-4 sm:px-8 pt-16 mt-8">
                    {/* Header Section */}
                    <div className="mb-12">
                      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-gray-300 pb-12">
                        <div className="flex-1">
                          <span className="text-[11px] font-semibold tracking-[0.1em] text-gray-500 uppercase mb-6 block">
                            {formData.tag || 'Design Philosophy'} • Issue {new Date().getMonth() + 1}
                          </span>
                          <h1 className="font-serif text-[clamp(40px,7vw,85px)] leading-[1.05] font-bold text-[#111] tracking-tight max-w-[800px]">
                            {formData.title || 'Your Title Here'}
                          </h1>
                        </div>
                        <div className="md:w-[300px] flex-shrink-0 border-l border-gray-300 pl-6 text-[13px] text-gray-500 italic">
                          Words by {formData.author || 'Author'} <br className="hidden md:block"/>
                          Date: {formData.date}
                        </div>
                      </div>
                    </div>

                    {/* Hero Banner Image */}
                    <div className="mb-24 relative group">
                      <div className="w-full h-[300px] md:h-[500px] bg-gray-200">
                        {formData.image1 ? <img src={formData.image1} alt="Hero" className="w-full h-full object-cover" /> : <div className="flex h-full items-center justify-center text-gray-500">Hover to add Wide Hero Image</div>}
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300 z-10">
                        <label className="text-white font-bold mb-2">Hero Image URL (image1)</label>
                        <input type="text" name="image1" value={formData.image1 || ""} onChange={handleChange} className="w-1/2 p-2 text-black rounded outline-none" placeholder="Image URL" />
                      </div>
                      
                      {/* Subtitle / Info Overlay */}
                      <div className="absolute bottom-6 left-6 md:bottom-12 md:left-12 bg-[#f9f8f6]/95 backdrop-blur-sm p-4 md:p-6 max-w-[400px] shadow-sm z-20 hover:ring-2 hover:ring-[#4d8a82] transition-all">
                         <label className="text-xs text-[#4d8a82] font-bold uppercase mb-2 block">Hero Overlay (info)</label>
                         <textarea rows="3" name="info" value={formData.info || ""} onChange={handleChange} className="w-full bg-transparent border-b border-dashed border-gray-300 p-2 font-sans text-[13px] text-[#333] focus:outline-none focus:border-[#4d8a82] resize-y" placeholder="Short info text here..."></textarea>
                      </div>
                    </div>

                    {/* First Content block (Drop Cap) */}
                    <div className="flex flex-col md:flex-row gap-8 md:gap-16 mb-24 max-w-[1000px] mx-auto items-start">
                      <div className="w-full md:w-[45%] flex-shrink-0 group relative">
                         <div className="w-full h-[400px] bg-gray-200">
                           {formData.image2 ? <img src={formData.image2} alt="Content" className="w-full h-full object-cover" /> : <div className="flex h-full items-center justify-center text-gray-500">Hover to add Image 2</div>}
                         </div>
                         <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300 z-10">
                            <label className="text-white font-bold mb-2 text-center px-4">Side Image URL (image2)</label>
                            <input type="text" name="image2" value={formData.image2 || ""} onChange={handleChange} className="w-3/4 p-2 text-black rounded outline-none" placeholder="Image URL" />
                         </div>
                      </div>
                      
                      <div className="w-full md:w-[55%] pt-4 hover:ring-2 hover:ring-[#4d8a82] p-4 rounded transition-all">
                        <label className="text-xs text-[#4d8a82] font-bold uppercase mb-2 block">Heading 1</label>
                        <input type="text" name="heading1" value={formData.heading1 || ""} onChange={handleChange} className="w-full bg-transparent border-b border-dashed border-gray-300 p-2 font-serif text-[24px] font-bold italic mb-4 text-[#111] focus:outline-none focus:border-[#4d8a82]" placeholder="Heading text..." />
                        
                        <label className="text-xs text-[#4d8a82] font-bold uppercase mb-2 block">First Text Block (para1 - Markdown, has drop cap)</label>
                        <textarea rows="8" name="para1" value={formData.para1 || ""} onChange={handleChange} className="w-full border border-dashed border-gray-300 p-3 font-sans text-[15px] leading-[1.8] text-[#444] focus:outline-none focus:border-[#4d8a82] resize-y" placeholder="Write text here..."></textarea>
                      </div>
                    </div>
                  </div>

                  {/* Blockquote Section */}
                  <div className="bg-[#f0ece6] py-20 px-4 mb-24 hover:ring-2 hover:ring-[#4d8a82] transition-all relative">
                    <div className="max-w-[800px] mx-auto text-center">
                      <div className="text-[#c8bba3] text-5xl font-serif mb-4 leading-none">❝</div>
                      <label className="text-xs text-[#4d8a82] font-bold uppercase mb-2 block">Blockquote Text (para4 - Markdown)</label>
                      <textarea rows="4" name="para4" value={formData.para4 || ""} onChange={handleChange} className="w-full bg-transparent border-b border-dashed border-gray-400 p-3 font-serif text-[24px] italic text-center text-[#222] focus:outline-none focus:border-[#4d8a82] resize-y mb-6" placeholder="Write quote here..."></textarea>
                      <div className="text-[12px] uppercase tracking-[0.2em] font-semibold text-gray-500">— {formData.author || "Author"}</div>
                    </div>
                  </div>

                  {/* Second Content block (Rhythm & Texture) */}
                  <div className="max-w-[1000px] mx-auto px-4 sm:px-8 mb-24 flex flex-col md:flex-row gap-12 md:gap-20 items-center">
                    <div className="w-full md:flex-1 hover:ring-2 hover:ring-[#4d8a82] p-4 rounded transition-all">
                      <label className="text-xs text-[#4d8a82] font-bold uppercase mb-2 block">Heading 2</label>
                      <input type="text" name="heading2" value={formData.heading2 || ""} onChange={handleChange} className="w-full bg-transparent border-b border-dashed border-gray-300 p-2 font-serif text-[24px] font-bold mb-4 text-[#111] focus:outline-none focus:border-[#4d8a82]" placeholder="Heading text..." />

                      <label className="text-xs text-[#4d8a82] font-bold uppercase mb-2 block">Second Text Block (para2 - Markdown)</label>
                      <textarea rows="8" name="para2" value={formData.para2 || ""} onChange={handleChange} className="w-full border border-dashed border-gray-300 p-3 font-sans text-[15px] leading-[1.8] text-[#444] focus:outline-none focus:border-[#4d8a82] resize-y" placeholder="Write text here..."></textarea>
                    </div>
                    
                    <div className="w-full md:w-[45%] flex-shrink-0 relative group">
                      <div className="absolute top-4 -left-4 w-full h-full bg-[#eee9e0] -z-10"></div>
                      <div className="w-full h-[300px] bg-gray-200 border-8 border-white shadow-sm">
                        {formData.image3 ? <img src={formData.image3} alt="Content" className="w-full h-full object-cover" /> : <div className="flex h-full items-center justify-center text-gray-500">Hover to add Image 3</div>}
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300 z-10">
                        <label className="text-white font-bold mb-2 text-center px-4">Image 3 URL (image3)</label>
                        <input type="text" name="image3" value={formData.image3 || ""} onChange={handleChange} className="w-3/4 p-2 text-black rounded outline-none" placeholder="Image URL" />
                      </div>
                    </div>
                  </div>

                  {/* Conclusion / Final Curation */}
                  <div className="max-w-[1000px] mx-auto px-4 sm:px-8 border-t border-gray-300 pt-16">
                    <div className="flex flex-col md:flex-row gap-8 md:gap-16 hover:ring-2 hover:ring-[#4d8a82] p-4 rounded transition-all">
                      <div className="md:w-1/3">
                        <h3 className="font-serif text-2xl italic font-bold text-[#111] mb-2">The Final Curation</h3>
                        <p className="text-[11px] text-gray-500 uppercase tracking-widest">Concluding Thoughts</p>
                      </div>
                      <div className="md:w-2/3">
                        <label className="text-xs text-[#4d8a82] font-bold uppercase mb-2 block">Conclusion Text (para3 - Markdown)</label>
                        <textarea rows="6" name="para3" value={formData.para3 || ""} onChange={handleChange} className="w-full border border-dashed border-gray-300 p-3 font-sans text-[15px] leading-[1.8] text-[#444] focus:outline-none focus:border-[#4d8a82] resize-y" placeholder="Write the conclusion here..."></textarea>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="pt-8 mb-12">
              <button type="submit" className="w-full bg-[#4d8a82] text-white font-bold py-4 text-xl flex justify-center items-center shadow-lg rounded-xl hover:bg-[#3d6e68] transition transform hover:-translate-y-1">
                {id ? 'Save Changes' : 'Publish Blog'}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}


